import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { id } from "@suite/core";
import type { FieldValue, FormField, LoadedDoc, PageRef, Stamp } from "./document.ts";
import { normalizeRotation, pageSize, rectToScreen, toPdf, toScreen, viewport, type Matrix } from "./geometry.ts";
import type { PageRender } from "./usePdfPages.ts";
import type { SavedSignature } from "./signatures.ts";

export interface EditViewProps {
  pages: PageRef[];
  docs: Map<string, LoadedDoc>;
  renderPage: (docId: string, pageNumber: number, rotation: number, width: number) => Promise<PageRender>;
  setFill: (docId: string, name: string, value: FieldValue) => void;
  addStamp: (pageId: string, stamp: Stamp) => void;
  updateStamp: (pageId: string, stampId: string, patch: Partial<Stamp>) => void;
  removeStamp: (pageId: string, stampId: string) => void;
  /** Artwork waiting to be placed with the next click on a page. */
  placing: SavedSignature | null;
  onPlaced: () => void;
  /** A signature field was clicked with nothing to place: let the parent open the picker. */
  onSignatureField: (pageId: string, rect: [number, number, number, number]) => void;
  /** Scroll this page into view when it changes. */
  focusPageId: string | null;
}

const MAX_RENDER_PX = 2400;

export function EditView(props: EditViewProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);

  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const measure = () => setWidth(Math.floor(el.getBoundingClientRect().width));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!props.focusPageId) return;
    document.getElementById(`edit-${props.focusPageId}`)?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [props.focusPageId]);

  // Delete removes the selected stamp; Escape drops selection and placement.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setSelected(null);
        if (props.placing) props.onPlaced();
      }
      if ((e.key === "Delete" || e.key === "Backspace") && selected) {
        const target = e.target as HTMLElement | null;
        if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
        const [pageId, stampId] = selected.split("|");
        if (pageId && stampId) props.removeStamp(pageId, stampId);
        setSelected(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected, props]);

  return (
    <div ref={listRef} className={`flex flex-col gap-6 ${props.placing ? "cursor-crosshair" : ""}`}>
      {props.placing ? (
        <p className="sticky top-3 z-20 mx-auto flex h-9 items-center gap-2 rounded-full border-2 border-teal-deep bg-teal px-4 text-[12px] font-semibold text-cream shadow-[0_6px_14px_-8px_rgba(31,63,57,0.6)]">
          Click where it should go — Esc to cancel
        </p>
      ) : null}
      {width > 0
        ? props.pages.map((page, index) => (
            <EditPage
              key={page.id}
              page={page}
              index={index}
              doc={props.docs.get(page.docId)}
              cssWidth={width}
              renderPage={props.renderPage}
              setFill={props.setFill}
              addStamp={props.addStamp}
              updateStamp={props.updateStamp}
              removeStamp={props.removeStamp}
              placing={props.placing}
              onPlaced={props.onPlaced}
              onSignatureField={props.onSignatureField}
              selected={selected}
              onSelect={setSelected}
            />
          ))
        : null}
    </div>
  );
}

// ---- one page -------------------------------------------------------------

interface EditPageProps {
  page: PageRef;
  index: number;
  doc: LoadedDoc | undefined;
  cssWidth: number;
  renderPage: EditViewProps["renderPage"];
  setFill: EditViewProps["setFill"];
  addStamp: EditViewProps["addStamp"];
  updateStamp: EditViewProps["updateStamp"];
  removeStamp: EditViewProps["removeStamp"];
  placing: SavedSignature | null;
  onPlaced: () => void;
  onSignatureField: EditViewProps["onSignatureField"];
  selected: string | null;
  onSelect: (key: string | null) => void;
}

function EditPage(p: EditPageProps) {
  const { page, doc, cssWidth } = p;
  const view = page.view ?? [0, 0, 595, 842];
  const rotation = normalizeRotation((page.intrinsicRotation ?? 0) + page.rotation);
  const size = pageSize(view, rotation);
  const scale = cssWidth / size.width;
  const cssHeight = Math.round(size.height * scale);
  const m = viewport(view, scale, rotation).transform;

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const renderedWidth = useRef(0);
  const bitmapRef = useRef<ImageBitmap | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => setVisible(entries.some((e) => e.isIntersecting)),
      { rootMargin: "700px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Render (or re-render) when visible and the needed resolution changed.
  useEffect(() => {
    if (!visible || !page.view) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const wanted = Math.min(MAX_RENDER_PX, Math.round(cssWidth * dpr));
    if (Math.abs(wanted - renderedWidth.current) / wanted < 0.2 && bitmapRef.current) return;
    let cancelled = false;
    p.renderPage(page.docId, page.sourcePage, rotation, wanted)
      .then(({ bitmap }) => {
        if (cancelled) {
          bitmap.close();
          return;
        }
        bitmapRef.current?.close();
        bitmapRef.current = bitmap;
        renderedWidth.current = wanted;
        const canvas = canvasRef.current;
        if (canvas) {
          canvas.width = bitmap.width;
          canvas.height = bitmap.height;
          canvas.getContext("2d")?.drawImage(bitmap, 0, 0);
        }
        setError(null);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [visible, cssWidth, rotation, page.docId, page.sourcePage, page.view, p.renderPage]);

  // Release GPU memory when the page scrolls far away, and on unmount.
  useEffect(() => {
    if (visible) return;
    bitmapRef.current?.close();
    bitmapRef.current = null;
    renderedWidth.current = 0;
    const canvas = canvasRef.current;
    if (canvas) canvas.width = canvas.height = 0;
  }, [visible]);
  useEffect(() => () => bitmapRef.current?.close(), []);

  const fields = doc?.fields.filter((f) => f.page === page.sourcePage) ?? [];

  const place = (clientX: number, clientY: number, fit?: [number, number, number, number]) => {
    if (!p.placing || !hostRef.current) return;
    const host = hostRef.current.getBoundingClientRect();
    let W: number, H: number, left: number, top: number;
    if (fit) {
      const box = rectToScreen(m, fit);
      // Fit inside the field, keeping the artwork's aspect ratio.
      W = Math.min(box.width, box.height * p.placing.aspect);
      H = W / p.placing.aspect;
      left = box.left + (box.width - W) / 2;
      top = box.top + (box.height - H) / 2;
    } else {
      W = Math.max(40, cssWidth * 0.28);
      H = W / p.placing.aspect;
      left = clientX - host.left - W / 2;
      top = clientY - host.top - H / 2;
    }
    const [x, y] = toPdf(m, left, top + H);
    const stamp: Stamp = {
      id: id("st"),
      sourceId: p.placing.id,
      png: p.placing.png,
      aspect: p.placing.aspect,
      x,
      y,
      width: W / scale,
      height: H / scale,
      rotation,
    };
    p.addStamp(page.id, stamp);
    p.onSelect(`${page.id}|${stamp.id}`);
    p.onPlaced();
  };

  return (
    <section id={`edit-${page.id}`} className="scroll-mt-4">
      <div className="mb-1.5 flex items-baseline justify-between">
        <span className="flex items-baseline gap-2">
          <span className="font-mono text-[11px] font-semibold text-pine">{String(p.index + 1).padStart(2, "0")}</span>
          {doc ? <span className="truncate font-mono text-[9px] uppercase tracking-[0.1em] text-muted">{doc.name}</span> : null}
        </span>
        {fields.length > 0 ? (
          <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-teal-deep">
            {fields.length} {fields.length === 1 ? "field" : "fields"}
          </span>
        ) : null}
      </div>
      <div
        ref={hostRef}
        className="relative overflow-hidden rounded-[6px] bg-white shadow-[0_1px_3px_rgba(51,41,31,0.18),0_12px_30px_-24px_rgba(46,30,16,0.6)]"
        style={{ width: cssWidth, height: cssHeight }}
        onPointerDown={(e) => {
          if (e.target === e.currentTarget || (e.target as HTMLElement).dataset["page"] === "1") {
            if (p.placing) place(e.clientX, e.clientY);
            else p.onSelect(null);
          }
        }}
      >
        <canvas ref={canvasRef} data-page="1" className="absolute inset-0 block h-full w-full" aria-hidden="true" />
        {error ? (
          <p className="absolute inset-x-0 top-1/2 -translate-y-1/2 px-6 text-center text-[13px] text-danger">{error}</p>
        ) : null}

        {fields.map((field) => (
          <FieldWidget
            key={field.id}
            field={field}
            m={m}
            scale={scale}
            value={doc?.fills[field.name]}
            onChange={(v) => p.setFill(page.docId, field.name, v)}
            onSignature={() => {
              if (p.placing) place(0, 0, field.rect);
              else p.onSignatureField(page.id, field.rect);
            }}
          />
        ))}

        {page.stamps.map((stamp) => (
          <StampBox
            key={stamp.id}
            stamp={stamp}
            m={m}
            scale={scale}
            displayRotation={rotation}
            selected={p.selected === `${page.id}|${stamp.id}`}
            onSelect={() => p.onSelect(`${page.id}|${stamp.id}`)}
            onChange={(patch) => p.updateStamp(page.id, stamp.id, patch)}
            onRemove={() => {
              p.removeStamp(page.id, stamp.id);
              p.onSelect(null);
            }}
          />
        ))}
      </div>
    </section>
  );
}

// ---- form widgets ---------------------------------------------------------

function FieldWidget({
  field,
  m,
  scale,
  value,
  onChange,
  onSignature,
}: {
  field: FormField;
  m: Matrix;
  scale: number;
  value: FieldValue | undefined;
  onChange: (v: FieldValue) => void;
  onSignature: () => void;
}) {
  const box = rectToScreen(m, field.rect);
  const style: React.CSSProperties = { left: box.left, top: box.top, width: box.width, height: box.height };
  const current = value === undefined ? field.initial : value;
  const base =
    "absolute rounded-[2px] border transition-colors focus:outline-none focus:ring-2 focus:ring-teal/40 disabled:opacity-60";

  if (field.kind === "signature") {
    return (
      <button
        type="button"
        style={style}
        onClick={onSignature}
        title="Add your signature here"
        className={`${base} flex items-center justify-center border-dashed border-teal/70 bg-teal/10 font-mono text-[10px] uppercase tracking-[0.14em] text-teal-deep hover:bg-teal/20`}
      >
        {box.height > 18 && box.width > 70 ? "Sign here" : null}
      </button>
    );
  }

  if (field.kind === "checkbox" || field.kind === "radio") {
    const checked = field.kind === "checkbox" ? current === true : current === field.onValue;
    return (
      <button
        type="button"
        role={field.kind}
        aria-checked={checked}
        aria-label={field.name}
        disabled={field.readOnly}
        style={style}
        onClick={() =>
          field.kind === "checkbox" ? onChange(!checked) : onChange(checked ? null : field.onValue ?? null)
        }
        className={`${base} flex items-center justify-center border-teal/60 bg-teal/10 text-pine hover:bg-teal/20 ${
          field.kind === "radio" ? "rounded-full" : ""
        }`}
      >
        {checked ? (
          field.kind === "radio" ? (
            <span className="block rounded-full bg-pine" style={{ width: "50%", height: "50%" }} />
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="h-[80%] w-[80%]" aria-hidden="true">
              <path d="m5 12 5 5L20 7" />
            </svg>
          )
        ) : null}
      </button>
    );
  }

  const declared = field.fontSize && field.fontSize > 0 ? field.fontSize * scale : 0;
  // Single-line boxes can't show text taller than themselves whatever the file
  // says; multiline keeps the file's size (that is what the export will use).
  const fontPx = field.multiline
    ? declared || 11 * scale
    : Math.min(declared || Math.min(box.height * 0.62, 18 * scale), box.height * 0.75);
  const textStyle: React.CSSProperties = {
    ...style,
    fontSize: Math.max(8, fontPx),
    lineHeight: field.multiline ? 1.3 : `${box.height}px`,
    padding: field.multiline ? `${2 * scale}px ${3 * scale}px` : `0 ${3 * scale}px`,
    textAlign: field.align ?? "left",
  };
  const cls = `${base} border-teal/50 bg-teal/[0.08] font-sans text-ink placeholder:text-muted/40 hover:bg-teal/15 focus:bg-white`;

  if (field.kind === "choice") {
    const options = field.options ?? [];
    if (field.multiSelect) {
      const chosen = Array.isArray(current) ? current : typeof current === "string" ? [current] : [];
      return (
        <select
          multiple
          aria-label={field.name}
          disabled={field.readOnly}
          style={textStyle}
          className={cls}
          value={chosen}
          onChange={(e) => onChange([...e.target.selectedOptions].map((o) => o.value))}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      );
    }
    const single = Array.isArray(current) ? current[0] ?? "" : typeof current === "string" ? current : "";
    return (
      <select
        aria-label={field.name}
        disabled={field.readOnly}
        style={textStyle}
        className={cls}
        value={single}
        onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
      >
        <option value="">—</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }

  const text = typeof current === "string" ? current : Array.isArray(current) ? current.join("\n") : "";
  if (field.multiline) {
    return (
      <textarea
        aria-label={field.name}
        disabled={field.readOnly}
        maxLength={field.maxLen}
        style={textStyle}
        className={`${cls} resize-none`}
        value={text}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <input
      type="text"
      aria-label={field.name}
      disabled={field.readOnly}
      maxLength={field.maxLen}
      style={textStyle}
      className={cls}
      value={text}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

// ---- stamps ---------------------------------------------------------------

function StampBox({
  stamp,
  m,
  scale,
  displayRotation,
  selected,
  onSelect,
  onChange,
  onRemove,
}: {
  stamp: Stamp;
  m: Matrix;
  scale: number;
  displayRotation: number;
  selected: boolean;
  onSelect: () => void;
  onChange: (patch: Partial<Stamp>) => void;
  onRemove: () => void;
}) {
  // The stamp's four corners in PDF space, then their screen bounding box. This
  // stays correct when the page has been rotated after the stamp was placed.
  const r = (stamp.rotation * Math.PI) / 180;
  const ux = Math.cos(r), uy = Math.sin(r);
  const vx = -Math.sin(r), vy = Math.cos(r);
  const pdfCorners: [number, number][] = [
    [stamp.x, stamp.y],
    [stamp.x + stamp.width * ux, stamp.y + stamp.width * uy],
    [stamp.x + stamp.width * ux + stamp.height * vx, stamp.y + stamp.width * uy + stamp.height * vy],
    [stamp.x + stamp.height * vx, stamp.y + stamp.height * vy],
  ];
  const corners = pdfCorners.map(([x, y]) => toScreen(m, x, y));
  const left = Math.min(...corners.map((c) => c[0]));
  const top = Math.min(...corners.map((c) => c[1]));
  const boxW = Math.max(...corners.map((c) => c[0])) - left;
  const boxH = Math.max(...corners.map((c) => c[1])) - top;
  const diff = normalizeRotation(displayRotation - stamp.rotation);
  const imgW = stamp.width * scale;
  const imgH = stamp.height * scale;
  // Created in an effect (not useState) so StrictMode's mount→unmount→mount in
  // dev doesn't revoke a URL the surviving component still uses.
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    const url = URL.createObjectURL(new Blob([stamp.png as BlobPart], { type: "image/png" }));
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [stamp.png]);

  const drag = useRef<{ x: number; y: number; sx: number; sy: number; w: number; h: number; mode: "move" | "resize" } | null>(null);

  const onPointerDown = (e: React.PointerEvent, mode: "move" | "resize") => {
    e.stopPropagation();
    e.preventDefault();
    onSelect();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { x: stamp.x, y: stamp.y, sx: e.clientX, sy: e.clientY, w: stamp.width, h: stamp.height, mode };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    if (d.mode === "move") {
      // Screen delta → PDF delta through the linear part of the transform.
      const [ox, oy] = toPdf(m, 0, 0);
      const [px, py] = toPdf(m, dx, dy);
      onChange({ x: d.x + (px - ox), y: d.y + (py - oy) });
    } else {
      // Grow along the screen diagonal, keeping aspect and the stamp's origin.
      const grow = diff % 180 === 0 ? dx / scale : dy / scale;
      const w = Math.max(12, d.w + grow);
      onChange({ width: w, height: w / stamp.aspect });
    }
  };
  const onPointerUp = () => {
    drag.current = null;
  };

  return (
    <div
      className={`absolute touch-none ${selected ? "z-10" : ""}`}
      style={{ left, top, width: boxW, height: boxH }}
      onPointerDown={(e) => onPointerDown(e, "move")}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {src ? (
      <img
        src={src}
        alt="Placed signature"
        draggable={false}
        className="pointer-events-none absolute left-1/2 top-1/2 max-w-none select-none"
        style={{ width: imgW, height: imgH, transform: `translate(-50%, -50%) rotate(${diff}deg)` }}
      />
      ) : null}
      <div
        className={`absolute inset-0 rounded-[3px] border transition-colors ${
          selected ? "border-teal-deep" : "border-transparent hover:border-teal/60"
        }`}
        style={{ cursor: "move" }}
      />
      {selected ? (
        <>
          <button
            type="button"
            aria-label="Remove this signature"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onRemove}
            className="absolute -right-3 -top-3 flex size-6 items-center justify-center rounded-full border border-rule bg-surface text-[13px] leading-none text-muted shadow hover:text-danger"
          >
            ×
          </button>
          <span
            role="presentation"
            onPointerDown={(e) => onPointerDown(e, "resize")}
            className="absolute -bottom-1.5 -right-1.5 size-3.5 cursor-nwse-resize rounded-full border-2 border-teal-deep bg-cream"
          />
        </>
      ) : null}
    </div>
  );
}
